import React, { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {
    indexIndividualFormsByTeacherType,
    clssInfo,
    getIstQuarter,
    getIIndQuarter,
    getIIIrdQuarter,
    getIVthQuarter,
    getVthQuarter,
    getVIthQuarter,
    FormEdit,
    DestroyForm
} from '../../Api'

import {
    Container,
    ContainerDivs,
    Header,
    Title,
    NewActivityButton,
    Toolbar,
    ToolbarActions,
    SearchInput,
    ToggleAllButton,
    EmptyState,
    MatterCard,
    MatterHeader,
    MatterTitle,
    Badge,
    ChevronWrap,
    MatterBody,
    BimesterCard,
    BimesterHeader,
    BimesterTitle,
    BimesterBody,
    ActivityMeta,
    ActivityActions,
    ActivityInfo,
    ActivityTitle,
    Tag,
    EditButton,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    Overlay,
    FormCard,
    FormHeader,
    FormTitle,
    FormSubtitle,
    FormGroup,
    FormLabel,
    FormSelect,
    FormError,
    FormActions,
    SaveButton,
    CancelButton,
    DeleteButton,
    SkeletonCard,
    SkeletonLine
} from './style'

// ── helpers fora do componente (não recriam a cada render) ──────────
const extractId = (v) => {
    if (!v) return null
    if (typeof v === 'string') {
        const t = v.trim()
        if ((t.startsWith('"') && t.endsWith('"')) ||
            (t.startsWith('[') && t.endsWith(']')) ||
            (t.startsWith('{') && t.endsWith('}'))) {
            try { v = JSON.parse(t) } catch { /* mantém v */ }
        }
    }
    if (Array.isArray(v)) v = v[0]
    if (v && typeof v === 'object') v = v._id ?? v.id ?? null
    const s = String(v ?? '').trim()
    return /^[a-fA-F0-9]{24}$/.test(s) ? s : null
}

const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

const bimOrder = (b) => parseInt(String(b).match(/\d+/)?.[0] || '99', 10)

// nome do aluno a partir do campo (populado ou não)
const studentName = (f) =>
    (f.id_student && typeof f.id_student === 'object' ? f.id_student.name : null)
    || f.studentName
    || 'Aluno'

// O model de relatório individual NÃO tem campo "bimonthly".
// O bimestre é derivado de qual campo id_*Quarter está preenchido.
// Mapeia cada chave de quarter para o rótulo de bimestre.
const QUARTER_FIELDS = [
    { field: 'id_iStQuarter', label: '1º BIMESTRE' },
    { field: 'id_iiNdQuarter', label: '2º BIMESTRE' },
    { field: 'id_iiiRdQuarter', label: '3º BIMESTRE' },
    { field: 'id_ivThQuarter', label: '4º BIMESTRE' },
    { field: 'id_vThQuarter', label: '5º BIMESTRE' },
    { field: 'id_viThQuarter', label: '6º BIMESTRE' }
]

// descobre o bimestre de um relatório a partir do campo de quarter preenchido
const formBimester = (f) => {
    for (const { field, label } of QUARTER_FIELDS) {
        if (f[field]) return label
    }
    return 'SEM BIMESTRE'
}

// trecho curto da descrição para o cabeçalho do card
// a description vem como HTML (<p>...</p>), então removemos as tags
const descPreview = (text, max = 90) => {
    const t = String(text || '')
        .replace(/<[^>]*>/g, ' ')   // tira tags HTML
        .replace(/&nbsp;/g, ' ')    // entidades comuns
        .replace(/\s+/g, ' ')       // normaliza espaços
        .trim()
    if (!t) return 'Sem descrição'
    return t.length > max ? `${t.slice(0, max)}…` : t
}

/* ── Item de relatório individual (memoizado) ────────────────────
   Evita re-render de todos os itens quando o usuário digita na busca
   ou abre/fecha outro bimestre.
   O card inteiro abre o modal de VISUALIZAÇÃO (sempre disponível,
   mesmo com período fechado). A edição fica dentro do modal e só é
   liberada se o bimestre estiver aberto para o tipo de professor.    */
const FormRowItem = memo(function FormRowItem({ form, onView }) {
    const nome = studentName(form)

    return (
        <MatterCard>
            <MatterHeader as="button" $open={false} onClick={() => onView(form)}>
                <ActivityInfo>
                    <ActivityTitle>{nome}</ActivityTitle>
                    <ActivityMeta>{descPreview(form.description)}</ActivityMeta>
                </ActivityInfo>

                <ActivityActions>
                    <EditButton
                        as="span"
                        onClick={(e) => { e.stopPropagation(); onView(form) }}
                        aria-label={`Ver relatório de ${nome}`}
                        title="Ver relatório completo"
                    >
                        Ver
                    </EditButton>
                </ActivityActions>
            </MatterHeader>
        </MatterCard>
    )
})

const IndividualForms = () => {
    const navigate = useNavigate()
    const location = useLocation()
    // funcionário (professor) escolhido pelo supervisor na tela anterior
    const { employee } = location.state || {}

    // ── listagem ─────────────────────────────────────────────────────
    const [forms, setForms] = useState([])
    const [loading, setLoading] = useState(true)             // dados principais
    const [loadingSecondary, setLoadingSecondary] = useState(true) // bimestres
    const [busca, setBusca] = useState('')
    const [debouncedBusca, setDebouncedBusca] = useState('')
    const [openBimesters, setOpenBimesters] = useState({})

    // ── modal de visualização / edição / exclusão do relatório ───────
    const [viewingForm, setViewingForm] = useState(null)
    const [modalMode, setModalMode] = useState('view')      // 'view' | 'edit' | 'delete'
    const [editedDescription, setEditedDescription] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [modalError, setModalError] = useState('')

    // ── modal de criação (seleciona bimestre/trimestre → individual-form) ─
    const [showCreate, setShowCreate] = useState(false)
    const [createBimonthly, setCreateBimonthly] = useState('')  // JSON string do quarter
    const [createError, setCreateError] = useState('')

    // ── contexto da turma/professor ──────────────────────────────────
    const [year, setYear] = useState('')
    //const [bimesterById, setBimesterById] = useState({})
    const [quartersList, setQuartersList] = useState([])     // objetos completos dos quarters
    const [assessmentRegime, setAssessmentRegime] = useState('')
    const [dailyStatus, setDailyStatus] = useState({})       // status de diário por bimestre
    const [isPhysicalTeacher, setIsPhysicalTeacher] = useState(false)
    const [totalStudents, setTotalStudents] = useState(0)    // total de alunos da turma

    const periodLabel = assessmentRegime === 'TRIMESTRAL' ? 'Trimestre' : 'Bimestre'
    const periodLabelLower = assessmentRegime === 'TRIMESTRAL' ? 'trimestre' : 'bimestre'

    // ── debounce da busca ────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300)
        return () => clearTimeout(t)
    }, [busca])

    // ── carga em DUAS FASES (carregamento progressivo) ───────────────
    // Fase 1: turma + relatórios  → libera a UI o quanto antes
    // Fase 2: bimestres → carregam em background (controle de período)
    useEffect(() => {
        let cancelled = false

            ; (async () => {
                setLoading(true)
                setLoadingSecondary(true)

                const id_class = sessionStorage.getItem('class-info')
                // supervisor: o funcionário vem do state da navegação (não do localStorage)
                const id_emp = employee
                const idSchool = safeParse(sessionStorage.getItem('id-school'))
                const physicalRaw = sessionStorage.getItem('physicalEducationTeacher')

                const myId = extractId(id_emp)
                const physicalId = extractId(safeParse(physicalRaw))

                const amIPhysical = !!physicalId && myId === physicalId
                const teacher_type = amIPhysical ? 'ED_FISICA' : 'REGENTE'

                try {
                    // ── FASE 1 ─────────────────────────────────────────
                    const resClass = await clssInfo(id_class)
                    if (cancelled) return

                    const turma = resClass?.data?.data?.[0]
                    const yearValue = turma?.year

                    sessionStorage.setItem('yearGrade', yearValue)

                    // dispara relatórios já com o year correto
                    const formPromise = indexIndividualFormsByTeacherType({
                        id_class,
                        year: yearValue,
                        teacher_type
                    })

                    // dispara dados secundários em PARALELO (mas sem bloquear)
                    const secondaryPromise = Promise.all([
                        getIstQuarter(yearValue, idSchool),
                        getIIndQuarter(yearValue, idSchool),
                        getIIIrdQuarter(yearValue, idSchool),
                        getIVthQuarter(yearValue, idSchool),
                        getVthQuarter(yearValue, idSchool),
                        getVIthQuarter(yearValue, idSchool)
                    ])

                    // espera só pelos relatórios (dado mais importante)
                    const resForm = await formPromise
                    if (cancelled) return

                    // a axios indexIndividualFormsByTeacherType retorna response.data,
                    // então o array já está em resForm.data (não resForm.data.data)
                    const list = resForm?.data?.data || resForm?.data || []

                    // libera a UI principal
                    setForms(list)
                    setYear(yearValue)
                    setDailyStatus(turma?.dailyStatus || {}) // status de diário por bimestre
                    setTotalStudents((turma?.id_student || []).filter(Boolean).length)
                    setIsPhysicalTeacher(amIPhysical)

                    // todos os bimestres iniciam fechados (recolhidos)

                    setLoading(false)

                    // ── FASE 2 ─────────────────────────────────────────
                    const [q1, q2, q3, q4, q5, q6] = await secondaryPromise
                    if (cancelled) return

                    const pickFirst = (q) => q?.data?.data?.find(r => r) || null
                    const i = pickFirst(q1)
                    const ii = pickFirst(q2)
                    const iii = pickFirst(q3)
                    const iv = pickFirst(q4)
                    const v = pickFirst(q5)
                    const vi = pickFirst(q6)

                    const map = {}
                    if (i) map['1º BIMESTRE'] = i
                    if (ii) map['2º BIMESTRE'] = ii
                    if (iii) map['3º BIMESTRE'] = iii
                    if (iv) map['4º BIMESTRE'] = iv
                    if (v) map['5º BIMESTRE'] = v
                    if (vi) map['6º BIMESTRE'] = vi

                    //setBimesterById(map)
                    setQuartersList([i, ii, iii, iv, v, vi].filter(Boolean))
                    setAssessmentRegime(i?.assessmentRegime || '')
                    setLoadingSecondary(false)
                } catch (err) {
                    console.error('Erro ao carregar página:', err)
                    if (!cancelled) {
                        setLoading(false)
                        setLoadingSecondary(false)
                    }
                }
            })()

        return () => { cancelled = true }
    }, [employee])

    // ── scroll lock + ESC quando algum modal está aberto ────────────
    useEffect(() => {
        const anyModal = !!viewingForm || showCreate
        if (!anyModal) return
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e) => {
            if (e.key === 'Escape' && !savingEdit && !deleting) {
                closeView()
                setShowCreate(false)
                setCreateError('')
            }
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKey)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingForm, showCreate, savingEdit, deleting])

    // ── status do diário: o bimestre está aberto para este professor? ─
    // Mesmo controle da página de conceitos: dailyStatus[bimonthly] traz
    // regentTeacher / physicalEducationTeacher; aberto quando !== 'fechado'.
    const isBimesterOpenFor = useCallback((bimonthly) => {
        const st = dailyStatus?.[bimonthly]
        if (!st) return true
        const field = isPhysicalTeacher ? st.physicalEducationTeacher : st.regentTeacher
        return field !== 'fechado'
    }, [dailyStatus, isPhysicalTeacher])

    // ── agrupamento: bimestre → aluno → relatórios ──────────────────
    const grouped = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const groups = {}

        for (const f of forms) {
            const bim = formBimester(f)
            const nome = studentName(f)

            if (q) {
                const haystack = [nome, f.description, bim]
                    .filter(Boolean).join(' ').toLowerCase()
                if (!haystack.includes(q)) continue
            }

            const studentId = (f.id_student && typeof f.id_student === 'object'
                ? f.id_student._id : f.id_student) || 'sem_aluno'

            if (!groups[bim]) {
                groups[bim] = { bimester: bim, students: {}, total: 0 }
            }
            if (!groups[bim].students[studentId]) {
                groups[bim].students[studentId] = {
                    id: studentId,
                    name: nome,
                    forms: [],
                    total: 0
                }
            }

            const s = groups[bim].students[studentId]
            s.forms.push(f)
            s.total++
            groups[bim].total++
        }

        return Object.values(groups)
            .sort((a, b) => bimOrder(a.bimester) - bimOrder(b.bimester))
            .map(b => ({
                ...b,
                studentsList: Object.values(b.students)
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                    .map(s => ({
                        ...s,
                        forms: s.forms
                            .slice()
                            .sort((a, b) =>
                                new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                    }))
            }))
    }, [forms, debouncedBusca])

    const totalForms = useMemo(
        () => grouped.reduce((acc, b) => acc + b.total, 0),
        [grouped]
    )

    // quarters disponíveis no select de criação.
    // Se trimestral, ignora o 4º (mesmo critério da página individual-form).
    const availableQuarters = useMemo(() => {
        return quartersList
            .filter(q => q && q.bimonthly)
            .filter(q => assessmentRegime === 'TRIMESTRAL'
                ? !String(q.bimonthly).includes('4º')
                : true)
            .sort((a, b) => bimOrder(a.bimonthly) - bimOrder(b.bimonthly))
    }, [quartersList, assessmentRegime])

    // ── toggles ──────────────────────────────────────────────────────
    const [openStudents, setOpenStudents] = useState({})

    const toggleStudent = useCallback((id) =>
        setOpenStudents(prev => ({ ...prev, [id]: !prev[id] })), [])

    const toggleBimester = useCallback((key) =>
        setOpenBimesters(prev => ({ ...prev, [key]: !prev[key] })), [])

    const allExpanded = useMemo(() => {
        if (grouped.length === 0) return false
        return grouped.every(b => {
            if (!openBimesters[b.bimester]) return false
            return b.studentsList.every(s =>
                openStudents[`${b.bimester}-${s.id}`]
            )
        })
    }, [grouped, openBimesters, openStudents])

    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setOpenBimesters({})
            setOpenStudents({})
        } else {
            const bims = {}, sts = {}
            for (const b of grouped) {
                bims[b.bimester] = true
                for (const s of b.studentsList) {
                    sts[`${b.bimester}-${s.id}`] = true
                }
            }
            setOpenBimesters(bims)
            setOpenStudents(sts)
        }
    }, [allExpanded, grouped])

    // ── navegação: criar / editar relatório ──────────────────────────
    // Salva o contexto no sessionStorage e redireciona para a página de
    // cadastro do relatório (mesmo padrão de navegação da página de conceito).
    // abre o modal de seleção de bimestre/trimestre
    const openCreate = useCallback(() => {
        setCreateBimonthly('')
        setCreateError('')
        setShowCreate(true)
    }, [])

    const closeCreate = useCallback(() => {
        setShowCreate(false)
        setCreateError('')
    }, [])

    // confirma a seleção: salva o contexto no sessionStorage e navega.
    // Mesmo padrão da página individual-form original (lê Selectbimonthly).
    const confirmCreate = useCallback(() => {
        if (!createBimonthly) {
            setCreateError(`Selecione um ${periodLabelLower}.`)
            return
        }

        let quarter
        try { quarter = JSON.parse(createBimonthly) } catch { quarter = null }
        if (!quarter?._id) {
            setCreateError('Seleção inválida. Tente novamente.')
            return
        }

        // bloqueia criar relatório em período fechado (por tipo de professor)
        if (!isBimesterOpenFor(quarter.bimonthly)) {
            setCreateError('O diário deste período está fechado. Contate o Supervisor.')
            return
        }

        const id_class = sessionStorage.getItem('class-info')

        // Selectbimonthly no formato que a página individual-form espera
        sessionStorage.setItem('Selectbimonthly', JSON.stringify({
            _id: quarter._id,
            bimonthly: quarter.bimonthly,
            statusSupervisor: quarter.statusSupervisor
        }))
        sessionStorage.setItem('assessmentRegime', assessmentRegime)
        sessionStorage.setItem('yearGrade', year)
        if (id_class) sessionStorage.setItem('class-info', id_class)

        setShowCreate(false)
        // supervisor: mantém o funcionário no state ao navegar para a tela de cadastro
        navigate('/Supervisor-individual-form', { state: { employee } })
    }, [createBimonthly, periodLabelLower, assessmentRegime, year, navigate, isBimesterOpenFor, employee])

    // abre o modal de visualização (sempre disponível, mesmo período fechado)
    const openView = useCallback((form) => {
        setViewingForm(form)
        setModalMode('view')
        setModalError('')
    }, [])

    const closeView = useCallback(() => {
        if (savingEdit || deleting) return
        setViewingForm(null)
        setModalMode('view')
        setModalError('')
        setEditedDescription('')
    }, [savingEdit, deleting])

    // entra no modo edição (ReactQuill). Só permite se o bimestre estiver aberto.
    const startEditing = useCallback(() => {
        if (!viewingForm) return
        const bim = formBimester(viewingForm)
        if (!isBimesterOpenFor(bim)) {
            setModalError('O diário deste período está fechado. Contate o Supervisor.')
            return
        }
        setEditedDescription(viewingForm.description || '')
        setModalError('')
        setModalMode('edit')
    }, [viewingForm, isBimesterOpenFor])

    // salva a edição via FormEdit (mesma assinatura da tela exist-form).
    // Atualiza em memória: tanto o relatório aberto quanto a lista.
    const handleSaveEdit = useCallback(async () => {
        if (savingEdit || !viewingForm) return

        // trava de segurança: período precisa continuar aberto
        if (!isBimesterOpenFor(formBimester(viewingForm))) {
            setModalError('O diário deste período está fechado. Contate o Supervisor.')
            return
        }

        setModalError('')
        setSavingEdit(true)
        try {
            const res = await FormEdit({
                update_idForm: viewingForm._id,
                editedDescription
            })

            if (res) {
                // atualiza a lista em memória (sem reload)
                setForms(prev => prev.map(f =>
                    f._id === viewingForm._id
                        ? { ...f, description: editedDescription }
                        : f
                ))
                // atualiza o relatório aberto e volta para o modo visualização
                setViewingForm(prev => prev
                    ? { ...prev, description: editedDescription }
                    : prev)
                setModalMode('view')
            } else {
                setModalError('Não foi possível salvar. Tente novamente.')
            }
        } catch (err) {
            console.error('Erro ao salvar relatório:', err)
            setModalError('Erro ao salvar o relatório. Tente novamente.')
        } finally {
            setSavingEdit(false)
        }
    }, [savingEdit, viewingForm, editedDescription, isBimesterOpenFor])

    // entra no modo confirmação de exclusão. Só permite com período aberto.
    const startDelete = useCallback(() => {
        if (!viewingForm) return
        if (!isBimesterOpenFor(formBimester(viewingForm))) {
            setModalError('O diário deste período está fechado. Contate o Supervisor.')
            return
        }
        setModalError('')
        setModalMode('delete')
    }, [viewingForm, isBimesterOpenFor])

    // confirma a exclusão via DestroyForm. Remove da lista (com anti-eco)
    // e fecha o modal.
    const handleDestroy = useCallback(async () => {
        if (deleting || !viewingForm) return

        if (!isBimesterOpenFor(formBimester(viewingForm))) {
            setModalError('O diário deste período está fechado. Contate o Supervisor.')
            return
        }

        const deletedId = viewingForm._id
        setModalError('')
        setDeleting(true)
        try {
            await DestroyForm(deletedId)
            // trata ausência de exceção como sucesso; remove da lista
            setForms(prev => prev.filter(f => f._id !== deletedId))
            setViewingForm(null)
            setModalMode('view')
        } catch (err) {
            console.error('Erro ao apagar relatório:', err)
            setModalError('Erro ao apagar o relatório. Tente novamente.')
        } finally {
            setDeleting(false)
        }
    }, [deleting, viewingForm, isBimesterOpenFor])

    // ── render ───────────────────────────────────────────────────────
    // Skeleton enquanto carrega o essencial (relatórios)
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <Title>Relatórios Individuais</Title>
                    </Header>
                    {[1, 2, 3].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="40%" $h="18px" />
                            <SkeletonLine $w="80%" />
                            <SkeletonLine $w="60%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <Title>Relatórios Individuais</Title>
                    <NewActivityButton
                        onClick={openCreate}
                        disabled={loadingSecondary}
                        title={loadingSecondary ? 'Carregando dados...' : 'Adicionar novo relatório'}
                    >
                        + Novo Relatório
                    </NewActivityButton>
                </Header>

                <Toolbar>
                    <SearchInput
                        type="text"
                        placeholder={`Buscar por aluno, descrição ou ${periodLabelLower}...`}
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        aria-label="Buscar relatórios individuais"
                    />
                    <ToolbarActions>
                        <Badge>
                            {totalForms} {totalForms === 1 ? 'relatório' : 'relatórios'}
                        </Badge>
                        {grouped.length > 0 && (
                            <ToggleAllButton onClick={toggleAll}>
                                {allExpanded ? 'Colapsar tudo' : 'Expandir tudo'}
                            </ToggleAllButton>
                        )}
                    </ToolbarActions>
                </Toolbar>

                {grouped.length === 0 ? (
                    <EmptyState>
                        {debouncedBusca
                            ? `Nenhum relatório encontrado para "${debouncedBusca}".`
                            : 'Nenhum relatório individual encontrado.'}
                    </EmptyState>
                ) : (
                    grouped.map((bim) => {
                        const bimOpen = !!openBimesters[bim.bimester]
                        // adicionados = alunos com relatório neste bimestre;
                        // faltam = total de alunos da turma menos os já adicionados
                        const adicionados = bim.studentsList.length
                        const faltam = Math.max(totalStudents - adicionados, 0)

                        return (
                            <BimesterCard key={bim.bimester}>
                                <BimesterHeader
                                    $open={bimOpen}
                                    onClick={() => toggleBimester(bim.bimester)}
                                    aria-expanded={bimOpen}
                                >
                                    <BimesterTitle>
                                        <span>
                                            Relatórios do {bim.bimester.replace('BIMESTRE', periodLabel.toUpperCase())}
                                        </span>
                                        <ActivityMeta>
                                            {totalStudents > 0 && (
                                                <Tag $variant="media">Alunos: {totalStudents}</Tag>
                                            )}
                                            <Tag $variant="tipo">Adicionados: {adicionados}</Tag>
                                            {totalStudents > 0 && (
                                                <Tag $variant={faltam > 0 ? 'c' : 'tipo'}>
                                                    {faltam > 0 ? `Faltam: ${faltam}` : 'Completo'}
                                                </Tag>
                                            )}

                                        </ActivityMeta>
                                    </BimesterTitle>
                                    <ActivityMeta>
                                        <ChevronWrap $open={bimOpen}>▼</ChevronWrap>
                                    </ActivityMeta>
                                </BimesterHeader>

                                {bimOpen && (
                                    <BimesterBody>
                                        {bim.studentsList.map((s) => {
                                            const studentKey = `${bim.bimester}-${s.id}`
                                            const studentOpen = !!openStudents[studentKey]

                                            return (
                                                <MatterCard key={studentKey}>
                                                    <MatterHeader
                                                        $open={studentOpen}
                                                        onClick={() => toggleStudent(studentKey)}
                                                        aria-expanded={studentOpen}
                                                    >
                                                        <MatterTitle>
                                                            {s.name}
                                                            <Badge>{s.total}</Badge>
                                                        </MatterTitle>
                                                        <ActivityMeta>
                                                            <Tag $variant="tipo">
                                                                {s.total} {s.total === 1 ? 'relatório' : 'relatórios'}
                                                            </Tag>
                                                            <ChevronWrap $open={studentOpen}>▼</ChevronWrap>
                                                        </ActivityMeta>
                                                    </MatterHeader>

                                                    {studentOpen && (
                                                        <MatterBody>
                                                            {s.forms.map((f) => (
                                                                <FormRowItem
                                                                    key={f._id}
                                                                    form={f}
                                                                    onView={openView}
                                                                />
                                                            ))}
                                                        </MatterBody>
                                                    )}
                                                </MatterCard>
                                            )
                                        })}
                                    </BimesterBody>
                                )}
                            </BimesterCard>
                        )
                    })
                )}

                <ToGoBack onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>

            {/* MODAL — visualização sempre disponível (mesmo período fechado).
                Editar e Apagar só ficam habilitados se o bimestre estiver aberto. */}
            {viewingForm && (() => {
                const bim = formBimester(viewingForm)
                const canEdit = isBimesterOpenFor(bim)
                const nome = studentName(viewingForm)
                const periodo = bim.replace('BIMESTRE', periodLabel.toUpperCase())

                return (
                    <Overlay onClick={closeView}>
                        <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

                            {/* ── MODO VISUALIZAÇÃO ───────────────────────── */}
                            {modalMode === 'view' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>{nome}</FormTitle>
                                        <FormSubtitle>{periodo}</FormSubtitle>
                                    </FormHeader>

                                    <div
                                        style={{
                                            color: '#003e4f',
                                            fontSize: '14px',
                                            lineHeight: 1.6,
                                            maxHeight: '50vh',
                                            overflowY: 'auto',
                                            marginBottom: '14px'
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: viewingForm.description || '<p>Sem descrição.</p>'
                                        }}
                                    />

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton onClick={closeView}>Fechar</CancelButton>
                                        <DeleteButton
                                            onClick={startDelete}
                                            disabled={!canEdit}
                                            title={canEdit ? 'Apagar relatório' : 'Período fechado'}
                                        >
                                            Apagar
                                        </DeleteButton>
                                        <SaveButton
                                            onClick={startEditing}
                                            disabled={!canEdit}
                                            title={canEdit ? 'Editar relatório' : 'Período fechado — contate o Supervisor'}
                                        >
                                            {canEdit ? 'Editar' : 'Período Fechado'}
                                        </SaveButton>
                                    </FormActions>
                                </>
                            )}

                            {/* ── MODO EDIÇÃO (ReactQuill) ────────────────── */}
                            {modalMode === 'edit' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>Editando Relatório</FormTitle>
                                        <FormSubtitle>{nome} · {periodo}</FormSubtitle>
                                    </FormHeader>

                                    <div style={{ marginBottom: '14px' }}>
                                        <ReactQuill
                                            theme="snow"
                                            modules={{
                                                toolbar: [
                                                    [{ font: [] }],
                                                    [{ list: 'ordered' }, { list: 'bullet' }],
                                                    ['bold', 'italic', 'underline'],
                                                    [{ color: [] }, { background: [] }],
                                                    ['clean']
                                                ]
                                            }}
                                            value={editedDescription}
                                            onChange={(content) => setEditedDescription(content)}
                                            placeholder="Ficha Individual do Aluno"
                                        />
                                    </div>

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton
                                            onClick={() => { setModalMode('view'); setModalError('') }}
                                            disabled={savingEdit}
                                        >
                                            Cancelar
                                        </CancelButton>
                                        <SaveButton onClick={handleSaveEdit} disabled={savingEdit}>
                                            {savingEdit ? 'Salvando...' : 'Salvar'}
                                        </SaveButton>
                                    </FormActions>
                                </>
                            )}

                            {/* ── MODO EXCLUSÃO ───────────────────────────── */}
                            {modalMode === 'delete' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>Apagar Relatório</FormTitle>
                                        <FormSubtitle>{nome} · {periodo}</FormSubtitle>
                                    </FormHeader>

                                    <p style={{ color: '#5b6b72', fontSize: '14px', lineHeight: 1.5, marginBottom: '14px' }}>
                                        Tem certeza que deseja apagar o relatório do aluno(a) <strong>{nome}</strong> no {periodo}? Esta ação não pode ser desfeita.
                                    </p>

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton
                                            onClick={() => { setModalMode('view'); setModalError('') }}
                                            disabled={deleting}
                                        >
                                            Cancelar
                                        </CancelButton>
                                        <DeleteButton onClick={handleDestroy} disabled={deleting}>
                                            {deleting ? 'Apagando...' : 'Apagar'}
                                        </DeleteButton>
                                    </FormActions>
                                </>
                            )}

                        </FormCard>
                    </Overlay>
                )
            })()}

            {/* MODAL DE NOVO RELATÓRIO — seleciona bimestre/trimestre → /Supervisor-individual-form */}
            {showCreate && (() => {
                // período selecionado está aberto? (para bloquear continuar)
                let selQuarter = null
                try { selQuarter = createBimonthly ? JSON.parse(createBimonthly) : null } catch { selQuarter = null }
                const createSelectedOpen = selQuarter?.bimonthly
                    ? isBimesterOpenFor(selQuarter.bimonthly)
                    : true

                return (
                    <Overlay onClick={closeCreate}>
                        <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                            <FormHeader>
                                <FormTitle>Novo Relatório</FormTitle>
                                <FormSubtitle>
                                    Selecione o {periodLabelLower} para adicionar o relatório
                                </FormSubtitle>
                            </FormHeader>

                            <FormGroup>
                                <FormLabel>{periodLabel}</FormLabel>
                                <FormSelect
                                    value={createBimonthly}
                                    onChange={(e) => { setCreateBimonthly(e.target.value); setCreateError('') }}
                                >
                                    <option value="">Selecione</option>
                                    {availableQuarters.length === 0 ? (
                                        <option disabled value="">
                                            Sem {periodLabelLower} configurado
                                        </option>
                                    ) : (
                                        availableQuarters.map(q => {
                                            const fechado = !isBimesterOpenFor(q.bimonthly)
                                            const label = assessmentRegime === 'TRIMESTRAL'
                                                ? String(q.bimonthly).replace('BIMESTRE', 'TRIMESTRE')
                                                : q.bimonthly
                                            return (
                                                <option
                                                    key={q._id}
                                                    value={JSON.stringify({
                                                        _id: q._id,
                                                        bimonthly: q.bimonthly,
                                                        statusSupervisor: q.statusSupervisor
                                                    })}
                                                >
                                                    {label}{fechado ? ' · FECHADO' : ''}
                                                </option>
                                            )
                                        })
                                    )}
                                </FormSelect>
                            </FormGroup>

                            {createBimonthly && !createSelectedOpen && (
                                <FormError>
                                    O diário deste período está fechado. Não é possível adicionar relatório.
                                </FormError>
                            )}

                            {createError && <FormError>{createError}</FormError>}

                            <FormActions>
                                <CancelButton onClick={closeCreate}>Cancelar</CancelButton>
                                <SaveButton
                                    onClick={confirmCreate}
                                    disabled={!!createBimonthly && !createSelectedOpen}
                                >
                                    Continuar
                                </SaveButton>
                            </FormActions>
                        </FormCard>
                    </Overlay>
                )
            })()}
        </Container>
    )
}

export default IndividualForms